import { authService } from '@/api/auth.service';
import type { LoginDto } from '@/entities/auth/login-dto.entity';
import { applyLanguageForSession } from '@/hooks/apply-language-for-session';
import { setBaseLanguage } from '@/hooks/use-temporary-locale';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { type MutationDefaultsContext, queryClient } from '../query-client';
import type { MaybeRefOrGetter } from 'vue';
import { AxiosError } from 'axios';
import type { RawRule } from '@casl/ability';
import type { TokenPayload } from '@/entities/auth/jwt-user.entity';

export { type TokenPayload } from '@/entities/auth/jwt-user.entity';

export const tokenPayloadOptions: Parameters<
  typeof queryClient.ensureQueryData<TokenPayload, Error, TokenPayload, string[]>
>[0] = {
  queryKey: ['token-payload'],
  queryFn: () => authService.getTokenPayload(),
  retry: (_, err) => {
    return err instanceof AxiosError && (err.code === 'ERR_NETWORK' || (err.status ?? 0) > 500);
  },
};

export const useTokenPayload = (enabled?: MaybeRefOrGetter<boolean>) => {
  return useQuery({
    ...tokenPayloadOptions,
    enabled,
  });
};

export const abilitiesOptions: Parameters<
  typeof queryClient.ensureQueryData<RawRule[], Error, RawRule[], string[]>
>[0] = {
  queryKey: ['abilities'],
  queryFn: () => authService.getAbilities(),
};

export const useAbilities = (enabled?: MaybeRefOrGetter<boolean>) => {
  return useQuery({
    ...abilitiesOptions,
    enabled,
  });
};

export const useLogin = () =>
  useMutation<void, unknown, LoginDto, MutationDefaultsContext>({
    mutationFn: ({ email, password }) => authService.login(email, password),
    onSuccess: async (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(
        null,
        null,
        {
          successMessage: 'Login successful',
        },
        context,
      );
      await invalidateAuthData();
    },
  });

export const useOAuthCallback = () =>
  useMutation({
    mutationFn: (queryString: string) => authService.oauthCallback(queryString),
    onSuccess: async (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(
        null,
        null,
        {
          successMessage: 'Login successful',
        },
        context,
      );
      await invalidateAuthData();
    },
  });

export async function invalidateAuthData() {
  await queryClient.invalidateQueries({ queryKey: ['token-payload'] });
  await queryClient.invalidateQueries({ queryKey: ['abilities'] });
}

export function applyLoggedInUserLanguage(userId: string) {
  return applyLanguageForSession(
    userId,
    async () => {
      const me = await queryClient.fetchQuery({
        queryKey: ['me'],
        queryFn: () => authService.getMe(),
      });
      return me?.preferredLocal;
    },
    setBaseLanguage,
  );
}
